import { useState, useEffect } from "react";
import {
  Box, Typography, TextField, Skeleton,
  Select, MenuItem, FormControl, Collapse,
} from "@mui/material";
import { Box as BoxIcon } from "@mynaui/icons-react";
import TuneIcon from "@mui/icons-material/Tune";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import TableRowsIcon from "@mui/icons-material/TableRows";
import WorkOrderCard from "../components/WorkOrderCard";
import dashboardService from "../../../services/dashboardService";
import { useZone } from '../../../shared/context/ZoneContext';
import Pagination from "../../assets/components/Pagination";

const PAGE_SIZE = 20;

const selectSx = {
  borderRadius: "8px",
  fontSize: "14px",
  height: "40px",
  background: "#FFFFFF",
  "& fieldset": { borderColor: "#E2E8F0" },
  "&:hover fieldset": { borderColor: "#CBD5E1" },
};

const colLabelSx = {
  fontSize: "13px",
  fontWeight: 600,
  color: "#374151",
  mb: "8px",
};

const capitalize = (str) =>
  str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : "";

const formatDate = (dateStr) => {
  if (!dateStr) return "N/A";
  return new Date(dateStr).toLocaleDateString("en-CA");
};

const mapToCardShape = (wo) => ({
  id:         wo.workOrderNumber,
  title:      wo.title       ?? "N/A",
  description:wo.description ?? "N/A",
  location:   `${wo.plantName} > ${wo.areaName} > ${wo.assetName}`,
  priority:   capitalize(wo.priority),
  status:     capitalize(wo.status),
  technician: wo.assignedTo?.name ?? "Unassigned",
  dueDate:    formatDate(wo.dueDate),
  cost:       wo.totalServiceCost ?? 0,
  parts:      wo.partsCount ?? 0,
});

const WorkOrderSkeleton = () => (
  <Box sx={{
    border: "1px solid #E2E8F0", borderRadius: "12px",
    padding: "20px", display: "flex", flexDirection: "column", gap: "12px",
  }}>
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      <Skeleton variant="text" width={140} height={24} />
      <Skeleton variant="rounded" width={70} height={24} sx={{ borderRadius: "20px" }} />
    </Box>
    <Skeleton variant="text" width="60%" height={20} />
    <Skeleton variant="text" width="80%" height={20} />
    <Skeleton variant="text" width="50%" height={20} />
    <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
      <Skeleton variant="text" width={80} height={20} />
      <Skeleton variant="text" width={80} height={20} />
    </Box>
  </Box>
);

export default function WorkOrdersPage() {
  const [workOrders, setWorkOrders]   = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading]         = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);          // ── new
  const { selectedZone } = useZone();
  const [priority, setPriority]       = useState("all");
  const [technician, setTechnician]   = useState("all");
  const [dueDateFrom, setDueDateFrom] = useState("");
  const [dueDateTo, setDueDateTo]     = useState("");

  useEffect(() => {
    if (!selectedZone) return;
    setLoading(true);
    setWorkOrders([]);
    dashboardService.getWorkOrders(selectedZone)
      .then((res) => {
        const mapped = (res.data?.data?.data?.workOrders ?? []).map(mapToCardShape);
        setWorkOrders(mapped);
      })
      .catch((err) => console.error("Failed to fetch work orders:", err.message))
      .finally(() => setLoading(false));
  }, [selectedZone]);

  // Reset to page 1 whenever filters/search change                // ── new
  useEffect(() => { setCurrentPage(1); }, [searchQuery, priority, technician, dueDateFrom, dueDateTo]);

  const technicians = [...new Set(workOrders.map((w) => w.technician).filter(Boolean))];

  const activeFilterCount = [
    priority   !== "all",
    technician !== "all",
    dueDateFrom !== "" || dueDateTo !== "",
  ].filter(Boolean).length;

  const filteredOrders = workOrders.filter((order) => {
    const matchesSearch =
      order.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
      order.title.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesPriority   = priority   === "all" || order.priority?.toLowerCase()   === priority.toLowerCase();
    const matchesTechnician = technician === "all" || order.technician === technician;
    const orderDate = order.dueDate && order.dueDate !== "N/A" ? new Date(order.dueDate) : null;
    const matchesDueDateFrom = !dueDateFrom || (orderDate && orderDate >= new Date(dueDateFrom));
    const matchesDueDateTo   = !dueDateTo   || (orderDate && orderDate <= new Date(dueDateTo));
    return matchesSearch && matchesPriority && matchesTechnician && matchesDueDateFrom && matchesDueDateTo;
  });

  // ── Pagination slice                                            // ── new
  const totalPages    = Math.ceil(filteredOrders.length / PAGE_SIZE);
  const paginatedOrders = filteredOrders.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100%" }}>
      <Box sx={{ px: { xs: 2, sm: 3, md: 4 }, py: { xs: 2, sm: 2.5, md: 3, lg: 4 } }}>
        <Box sx={{
          background: "#FFFFFF",
          border: "1px solid #E2E8F0",
          borderRadius: "12px",
          overflow: "hidden",
        }}>

          {/* ── Header Row ── */}
          <Box sx={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            flexWrap: "wrap", gap: 2,
            px: { xs: "16px", sm: "24px" }, py: { xs: "14px", sm: "16px" },
          }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <BoxIcon size={22} />
              <Typography sx={{ fontWeight: 600, fontSize: { xs: "16px", sm: "18px" }, color: "#020618" }}>
                Work Orders
              </Typography>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap", flex: { xs: "1 1 100%", sm: "0 0 auto" } }}>
              <TextField
                placeholder="Search"
                size="small"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{
                  flex: { xs: 1, sm: "0 0 auto" },
                  width: { sm: "260px", md: "360px" },
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px", fontSize: "14px", height: "40px",
                    "& fieldset": { borderColor: "#E2E8F0" },
                    "&:hover fieldset": { borderColor: "#CBD5E1" },
                  },
                }}
              />
              <Box sx={{
                width: 40, height: 40, display: "flex", alignItems: "center", justifyContent: "center",
                border: "1px solid #E2E8F0", borderRadius: "8px",
                background: "#1E293B", cursor: "pointer", flexShrink: 0,
              }}>
                <ViewModuleIcon sx={{ fontSize: 18, color: "#FFFFFF" }} />
              </Box>
              <Box sx={{
                width: 40, height: 40, display: "flex", alignItems: "center", justifyContent: "center",
                border: "1px solid #E2E8F0", borderRadius: "8px",
                background: "#FFFFFF", cursor: "pointer", flexShrink: 0,
              }}>
                <TableRowsIcon sx={{ fontSize: 18, color: "#64748B" }} />
              </Box>
              <Box
                onClick={() => setFiltersOpen((p) => !p)}
                sx={{
                  display: "flex", alignItems: "center", gap: "8px",
                  px: "16px", height: "40px", flexShrink: 0,
                  borderRadius: "8px", background: "#1E293B",
                  cursor: "pointer", userSelect: "none",
                  "&:hover": { background: "#334155" },
                }}
              >
                <TuneIcon sx={{ fontSize: 16, color: "#FFFFFF" }} />
                <Typography sx={{ fontSize: "14px", fontWeight: 500, color: "#FFFFFF" }}>
                  Filters
                </Typography>
                {activeFilterCount > 0 && (
                  <Box sx={{
                    width: "18px", height: "18px", background: "#3B82F6",
                    borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    <Typography sx={{ fontSize: "10px", fontWeight: 700, color: "#FFF" }}>
                      {activeFilterCount}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>

          {/* ── Filter Panel ── */}
          <Collapse in={filtersOpen}>
            <Box sx={{
              px: { xs: "16px", sm: "24px" }, py: "20px",
              background: "#F8FAFC",
              border: "1px solid #E2E8F0", borderLeft: "none", borderRight: "none",
              display: "flex", flexWrap: "wrap", gap: "16px",
            }}>
              <Box sx={{ flex: "1 1 180px", minWidth: "160px" }}>
                <Typography sx={colLabelSx}>Priority</Typography>
                <FormControl fullWidth size="small">
                  <Select value={priority} onChange={(e) => setPriority(e.target.value)} sx={selectSx}>
                    <MenuItem value="all">All Priorities</MenuItem>
                    <MenuItem value="high">High</MenuItem>
                    <MenuItem value="medium">Medium</MenuItem>
                    <MenuItem value="low">Low</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ flex: "1 1 180px", minWidth: "160px" }}>
                <Typography sx={colLabelSx}>Technician</Typography>
                <FormControl fullWidth size="small">
                  <Select value={technician} onChange={(e) => setTechnician(e.target.value)} sx={selectSx}>
                    <MenuItem value="all">All Technicians</MenuItem>
                    {technicians.map((t) => (
                      <MenuItem key={t} value={t}>{t}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ flex: "1 1 280px", minWidth: "240px" }}>
                <Typography sx={colLabelSx}>Due Date</Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <TextField
                    size="small" type="date" value={dueDateFrom}
                    onChange={(e) => setDueDateFrom(e.target.value)}
                    sx={{ flex: 1, "& .MuiOutlinedInput-root": { borderRadius: "8px", fontSize: "14px", height: "40px", background: "#FFFFFF", "& fieldset": { borderColor: "#E2E8F0" }, "&:hover fieldset": { borderColor: "#CBD5E1" } } }}
                  />
                  <Typography sx={{ color: "#94A3B8", fontSize: "14px", flexShrink: 0 }}>–</Typography>
                  <TextField
                    size="small" type="date" value={dueDateTo}
                    onChange={(e) => setDueDateTo(e.target.value)}
                    sx={{ flex: 1, "& .MuiOutlinedInput-root": { borderRadius: "8px", fontSize: "14px", height: "40px", background: "#FFFFFF", "& fieldset": { borderColor: "#E2E8F0" }, "&:hover fieldset": { borderColor: "#CBD5E1" } } }}
                  />
                </Box>
              </Box>
            </Box>
          </Collapse>

          {/* ── Cards Grid ── */}
          <Box sx={{ p: { xs: "16px", sm: "24px" } }}>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: "20px" }}>
              {loading ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <Box key={i} sx={{ flex: { xs: "1 1 100%", md: "1 1 calc(50% - 10px)" }, maxWidth: { xs: "100%", md: "calc(50% - 10px)" } }}>
                    <WorkOrderSkeleton />
                  </Box>
                ))
              ) : filteredOrders.length === 0 ? (
                <Typography sx={{ color: "#62748E", fontSize: "14px" }}>
                  No work orders found.
                </Typography>
              ) : (
                paginatedOrders.map((order) => (       // ── changed filteredOrders → paginatedOrders
                  <Box key={order.id} sx={{ flex: { xs: "1 1 100%", md: "1 1 calc(50% - 10px)" }, maxWidth: { xs: "100%", md: "calc(50% - 10px)" } }}>
                    <WorkOrderCard workOrder={order} />
                  </Box>
                ))
              )}
            </Box>

            {/* ── Pagination ── */}
            {!loading && totalPages > 1 && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                totalItems={filteredOrders.length}
                itemsPerPage={PAGE_SIZE}
              />
            )}
          </Box>

        </Box>
      </Box>
    </Box>
  );
}